import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Form, Input, Select, Button, DatePicker, message } from 'antd';
import {
	UploadOutlined,
	FilePdfOutlined,
	EditOutlined,
	SwapOutlined,
	EyeOutlined,
} from '@ant-design/icons';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Box, Flex, Text } from 'atoms';
import { updateDriverV2, getDriverByIdV2, transferDriverV2 } from 'store/driverSliceV2';
import { searchVehiclesV2 } from 'store/vehicleSliceV2';
import { searchVendorV2 } from 'store/vendorSliceV2';
import { getSignedUrl, uploadFilesToS3 } from 'store/commonSlice';
import { TransferDriverModal } from 'componentsv2/modals';
import { DocumentViewerModal } from 'componentsv2/common/modals';

const { Option } = Select;

const PageContainer = styled(Box)`
	max-width: 1600px;
	margin: 0 auto;
	padding: 24px;
	background: #f4f6f8;
	min-height: 100vh;
`;

const FormContainer = styled(Box)`
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 32px 40px;
	margin-bottom: 24px;
`;

const SectionTitle = styled(Text)`
	font-size: 20px;
	font-weight: 600;
	color: #333;
	display: block;
	margin-bottom: 8px;
`;

const SectionSubtitle = styled(Text)`
	font-size: 14px;
	color: #666;
	display: block;
	margin-bottom: 24px;
`;

const FormRow = styled(Flex)`
	margin-bottom: 24px;
	gap: 24px;

	&:last-of-type {
		margin-bottom: 0;
	}
`;

const FormItem = styled(Box)`
	flex: 1;

	.ant-form-item-label {
		padding-bottom: 8px;

		label {
			font-size: 13px;
			font-weight: 500;
			color: #333;

			&::before {
				display: none !important;
			}
		}

		label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::after {
			display: inline-block;
			margin-left: 4px;
			color: #ff4d4f;
			font-size: 13px;
			line-height: 1;
			content: '*';
		}
	}

	.ant-input,
	.ant-select-selector,
	.ant-picker {
		border-radius: 4px;
		height: 40px;
		font-size: 13px;
	}
`;

const BrowseButton = styled(Button)`
	background: #18405d;
	border-color: #18405d;
	color: #fff;
	height: 40px;
	padding: 0 24px;
	font-weight: 500;
	border-radius: 4px;

	&:hover {
		background: #1e4a6b;
		border-color: #1e4a6b;
		color: #fff;
	}
`;

const ActionButtons = styled(Flex)`
	justify-content: flex-end;
	gap: 16px;
	margin-top: 24px;
`;

const CancelButton = styled(Button)`
	height: 40px;
	padding: 0 32px;
	font-weight: 500;
	border-radius: 4px;
	border-color: #d9d9d9;
	color: #333;

	&:hover {
		border-color: #40a9ff;
		color: #40a9ff;
	}
`;

const SaveButton = styled(Button)`
	background: #18405d;
	border-color: #18405d;
	color: #fff;
	height: 40px;
	padding: 0 32px;
	font-weight: 500;
	border-radius: 4px;

	&:hover {
		background: #1e4a6b;
		border-color: #1e4a6b;
		color: #fff;
	}
`;

const FileUploadContainer = styled(Box)`
	.ant-upload {
		width: 100%;
	}
`;

const HeaderSection = styled(Flex)`
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 24px;
`;

const TransferButton = styled(Button)`
	background-color: #e0f2fe;
	border: 1px solid #0ea5e9;
	color: #0369a1;
	height: 40px;
	padding: 0 24px;
	font-weight: 500;
	border-radius: 6px;
	display: flex;
	align-items: center;
	gap: 8px;

	&:hover {
		background-color: #bae6fd !important;
		border-color: #0284c7 !important;
		color: #0369a1 !important;
	}
`;

const EditButton = styled(Button)`
	background-color: #1e40af;
	border-color: #1e40af;
	color: white;
	height: 40px;
	padding: 0 24px;
	font-weight: 500;
	border-radius: 6px;
	display: flex;
	align-items: center;
	gap: 8px;

	&:hover {
		background-color: #1e3a8a !important;
		border-color: #1e3a8a !important;
		color: white !important;
	}
`;

// Constants for file validation
const ALLOWED_FILE_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
const MAX_FILE_SIZE = 5242880; // 5MB
const FILE_TYPE_ERROR_MSG = 'Please upload PDF, PNG, or JPEG file';
const FILE_SIZE_ERROR_MSG = 'File size must be less than 5MB';
const UPLOAD_FAILED_MSG = 'Failed to upload';
const FILE_UPLOAD_ERROR_LOG = 'File upload error:';
const ALL_VEHICLE_TYPES_VALUE = '__ALL_VEHICLE_TYPES__';

export default function EditDriverPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id } = useParams();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [vendorOptions, setVendorOptions] = useState([]);
	const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
	const [initialVehicleTypeIds, setInitialVehicleTypeIds] = useState([]);
	const [drivingLicenseFile, setDrivingLicenseFile] = useState(null);
	const [drivingLicenseFileKey, setDrivingLicenseFileKey] = useState(null);
	const [uploadingLicense, setUploadingLicense] = useState(false);
	const [transferModalVisible, setTransferModalVisible] = useState(false);
	const [driverDataForTransfer, setDriverDataForTransfer] = useState(null);
	const [transferring, setTransferring] = useState(false);
	const [documentViewerVisible, setDocumentViewerVisible] = useState(false);
	const [viewingDocumentUrl, setViewingDocumentUrl] = useState(null);
	const [viewingDocumentName, setViewingDocumentName] = useState('');
	const [viewingDocumentType, setViewingDocumentType] = useState('unknown');
	const fileInputRef = useRef(null);

	// Fetch vendor options
	useEffect(() => {
		dispatch(
			searchVendorV2({
				filter_by: { is_active: true },
				pagination: { page: 1, per_page: 1000 },
			})
		).then((response) => {
			if (response?.rows) {
				const options = response.rows.map((vendor) => ({
					value: vendor.id,
					label: vendor.vendor_name || vendor.name || `Vendor ${vendor.id}`,
				}));
				setVendorOptions(options);
			}
		});
	}, [dispatch]);

	const fetchVehicleTypeOptionsByVendor = async (vendorId) => {
		if (!vendorId) {
			setVehicleTypeOptions([]);
			return;
		}

		try {
			const response = await dispatch(
				searchVehiclesV2({
					filter_by: { vendor_id: vendorId, is_active: true },
					pagination: { page: 1, per_page: 1000 },
				})
			);

			const typeMap = new Map();
			(response?.rows || []).forEach((vehicle) => {
				if (
					vehicle.vehicle_type_id &&
					vehicle.vehicle_type_name &&
					!typeMap.has(vehicle.vehicle_type_id)
				) {
					typeMap.set(vehicle.vehicle_type_id, {
						value: vehicle.vehicle_type_id,
						label: vehicle.vehicle_type_name,
					});
				}
			});

			setVehicleTypeOptions(Array.from(typeMap.values()));
		} catch (error) {
			console.error('Error fetching vehicle types:', error);
			setVehicleTypeOptions([]);
		}
	};

	const handleVehicleTypeChange = (selectedValues = []) => {
		if (!selectedValues.includes(ALL_VEHICLE_TYPES_VALUE)) {
			return;
		}

		form.setFieldsValue({
			vehicle_type_ids: vehicleTypeOptions.map((type) => type.value),
		});
	};

	// Fetch driver data
	useEffect(() => {
		if (!id) {
			message.error('Driver ID is required');
			navigate('/drivers');
			return;
		}

		setLoading(true);
		dispatch(getDriverByIdV2({ id }))
			.then((driver) => {
				if (!driver) {
					message.error('Driver not found');
					navigate('/drivers');
					return;
				}

				const initialTypes = [].concat(driver.vehicle_type_ids || []);
				setInitialVehicleTypeIds(initialTypes);

				// Set form values
				form.setFieldsValue({
					vendor_id: driver.vendor_id,
					vehicle_type_ids: initialTypes,
					driver_name: driver.driver_name,
					mobile_number: driver.mobile_number,
					driving_license_number: driver.document_info?.driving_license?.number || '',
					driving_license_valid_till: driver.document_info?.driving_license?.valid_till
						? dayjs(driver.document_info.driving_license.valid_till)
						: null,
				});

				// Set file keys
				setDrivingLicenseFileKey(
					driver.document_info?.driving_license?.document_key || null
				);

				// Store driver data for transfer modal
				setDriverDataForTransfer({
					id: driver.id,
					driver_name: driver.driver_name,
					vendor_id: driver.vendor_id,
					vendor_name: driver.vendor_name,
				});
				fetchVehicleTypeOptionsByVendor(driver.vendor_id);

				setLoading(false);
			})
			.catch((error) => {
				console.error('Error fetching driver:', error);
				message.error('Failed to load driver data');
				setLoading(false);
				navigate('/drivers');
			});
	}, [id, dispatch, form, navigate]);

	// Handle driving license file change
	const handleFileChange = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		if (!ALLOWED_FILE_TYPES.includes(file.type)) {
			message.error(FILE_TYPE_ERROR_MSG);
			return;
		}

		if (file.size > MAX_FILE_SIZE) {
			message.error(FILE_SIZE_ERROR_MSG);
			return;
		}

		setDrivingLicenseFile(file);
		setUploadingLicense(true);

		try {
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'driver',
						keys: [file.name],
					},
				})
			);

			if (signedUrlResponse?.data?.[0]) {
				const signedUrl = signedUrlResponse.data[0];

				await dispatch(
					uploadFilesToS3({
						url: signedUrl.url,
						data: file,
					})
				);

				setDrivingLicenseFileKey(signedUrl.filename);
				setUploadingLicense(false);
				message.success('Driving license uploaded successfully');
			}
		} catch (error) {
			console.error(FILE_UPLOAD_ERROR_LOG, error);
			setUploadingLicense(false);
			message.error(`${UPLOAD_FAILED_MSG} driving license`);
		}
	};

	// Handle form submit
	const handleSubmit = async () => {
		try {
			const values = await form.validateFields([
				'vendor_id',
				'vehicle_type_ids',
				'driver_name',
				'mobile_number',
				'driving_license_number',
				'driving_license_valid_till',
			]);

			if (!drivingLicenseFileKey) {
				message.error('Please upload driving license document');
				return;
			}

			setSaving(true);
			const selectedVehicleTypeIds = (values.vehicle_type_ids || []).filter(
				(typeId) => typeId !== ALL_VEHICLE_TYPES_VALUE
			);

			const payload = {
				id,
				vendor_id: values.vendor_id,
				vehicle_type_ids: selectedVehicleTypeIds,
				driver_name: values.driver_name,
				mobile_number: values.mobile_number,
				document_info: {
					driving_license: {
						document_key: drivingLicenseFileKey,
						valid_till: values.driving_license_valid_till
							? dayjs(values.driving_license_valid_till).format('YYYY-MM-DD')
							: null,
						number: values.driving_license_number || '',
					},
				},
			};

			await dispatch(
				updateDriverV2({
					req_data: payload,
					setErrors: (errors) => {
						console.error('Validation errors:', errors);
					},
				})
			);

			message.success('Driver updated successfully');
			navigate('/drivers');
		} catch (error) {
			console.error('Submit error:', error);
			message.error('Failed to update driver');
			setSaving(false);
		}
	};

	// Handle edit mode toggle
	const handleEdit = () => {
		setIsEditMode(true);
	};

	// Handle transfer
	const handleTransfer = () => {
		if (!driverDataForTransfer) {
			message.error('Driver data not loaded');
			return;
		}
		setTransferModalVisible(true);
	};

	// Handle transfer confirmation
	const handleTransferConfirm = async (payload) => {
		try {
			setTransferring(true);
			await dispatch(
				transferDriverV2({
					req_data: payload,
					setErrors: (errors) => {
						console.error('Transfer validation errors:', errors);
					},
				})
			);
			setTransferModalVisible(false);
			message.success('Driver transfer request submitted successfully');
			// Refresh driver data to get updated vendor info
			dispatch(getDriverByIdV2({ id })).then((driver) => {
				if (driver) {
					form.setFieldsValue({
						vendor_id: driver.vendor_id,
						vehicle_type_ids: [].concat(driver.vehicle_type_ids || []),
						driver_name: driver.driver_name,
						mobile_number: driver.mobile_number,
						driving_license_number: driver.document_info?.driving_license?.number || '',
						driving_license_valid_till: driver.document_info?.driving_license
							?.valid_till
							? dayjs(driver.document_info.driving_license.valid_till)
							: null,
					});
					setDriverDataForTransfer({
						id: driver.id,
						driver_name: driver.driver_name,
						vendor_id: driver.vendor_id,
						vendor_name: driver.vendor_name,
					});
					fetchVehicleTypeOptionsByVendor(driver.vendor_id);
				}
			});
		} catch (error) {
			console.error('Transfer error:', error);
			message.error('Failed to transfer driver');
		} finally {
			setTransferring(false);
		}
	};

	// Handle transfer modal cancel
	const handleTransferCancel = () => {
		setTransferModalVisible(false);
	};

	// Handle viewing document
	const handleViewDocument = async (documentKey, documentName = 'Document') => {
		if (!documentKey) {
			message.error('No document key provided');
			return;
		}

		try {
			// Clean document key - remove S3 path prefix if present
			let cleanKey = documentKey;
			if (cleanKey.startsWith('s3://')) {
				cleanKey = cleanKey.replace(/^s3:\/\/[^/]+\//, '');
			}
			cleanKey = cleanKey.replace(/^\/+/, '');

			// Get signed URL for viewing
			const response = await dispatch(
				getSignedUrl({
					url_type: 'view',
					data: {
						entity: 'driver',
						keys: [cleanKey],
					},
				})
			);

			const documentUrl =
				(response && response.status === 'success' && response.data?.[0]?.url) ||
				(response?.data &&
					Array.isArray(response.data) &&
					response.data.length > 0 &&
					response.data[0]?.url)
					? response.data[0].url
					: null;

			if (documentUrl) {
				// Detect document type from file extension
				const extension = cleanKey.split('.').pop()?.toLowerCase();
				let docType = 'unknown';
				const lowerKey = cleanKey.toLowerCase();
				if (lowerKey.includes('.pdf') || extension === 'pdf') {
					docType = 'pdf';
				} else if (
					lowerKey.includes('.jpg') ||
					lowerKey.includes('.jpeg') ||
					lowerKey.includes('.png') ||
					['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)
				) {
					docType = 'image';
				}

				setViewingDocumentUrl(documentUrl);
				setViewingDocumentName(documentName);
				setViewingDocumentType(docType);
				setDocumentViewerVisible(true);
			} else {
				message.error('Failed to load document');
			}
		} catch (error) {
			console.error('Error viewing document:', error);
			message.error('Failed to view document');
		}
	};

	// Handle close document viewer
	const handleCloseDocumentViewer = () => {
		setDocumentViewerVisible(false);
		setViewingDocumentUrl(null);
		setViewingDocumentName('');
		setViewingDocumentType('unknown');
	};

	// Handle cancel
	const handleCancel = () => {
		if (isEditMode) {
			setIsEditMode(false);
			// Reset form to original values
			dispatch(getDriverByIdV2({ id })).then((driver) => {
				if (driver) {
					const initialTypes = [].concat(driver.vehicle_type_ids || []);
					setInitialVehicleTypeIds(initialTypes);

					form.setFieldsValue({
						vendor_id: driver.vendor_id,
						vehicle_type_ids: initialTypes,
						driver_name: driver.driver_name,
						mobile_number: driver.mobile_number,
						driving_license_number: driver.document_info?.driving_license?.number || '',
						driving_license_valid_till: driver.document_info?.driving_license
							?.valid_till
							? dayjs(driver.document_info.driving_license.valid_till)
							: null,
					});
					setDrivingLicenseFileKey(
						driver.document_info?.driving_license?.document_key || null
					);
					setDrivingLicenseFile(null);
					fetchVehicleTypeOptionsByVendor(driver.vendor_id);
				}
			});
		} else {
			navigate('/drivers');
		}
	};

	if (loading) {
		return (
			<PageContainer>
				<FormContainer>
					<Text>Loading driver data...</Text>
				</FormContainer>
			</PageContainer>
		);
	}

	return (
		<PageContainer>
			<FormContainer>
				<HeaderSection>
					<Box>
						<SectionTitle>Edit Driver</SectionTitle>
						<SectionSubtitle>Update driver information</SectionSubtitle>
					</Box>
					<Flex gap="12px">
						<TransferButton icon={<SwapOutlined />} onClick={handleTransfer}>
							Transfer
						</TransferButton>
						<EditButton icon={<EditOutlined />} onClick={handleEdit}>
							Edit
						</EditButton>
					</Flex>
				</HeaderSection>

				<Form form={form} layout="vertical">
					<FormRow>
						<FormItem>
							<Form.Item
								label="Select Vendor"
								name="vendor_id"
								rules={[{ required: true, message: 'Please select vendor' }]}
							>
								<Select placeholder="Select" size="large" showSearch disabled>
									{vendorOptions.map((vendor) => (
										<Option key={vendor.value} value={vendor.value}>
											{vendor.label}
										</Option>
									))}
								</Select>
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item
								label="Driver Name"
								name="driver_name"
								rules={[{ required: true, message: 'Please enter driver name' }]}
							>
								<Input
									placeholder="Eg : Venkatesh......."
									size="large"
									maxLength={255}
									disabled={!isEditMode}
								/>
							</Form.Item>
						</FormItem>
					</FormRow>

					<FormRow>
						<FormItem>
							<Form.Item
								label="Vehicle Type"
								name="vehicle_type_ids"
								rules={[{ required: true, message: 'Please select vehicle type' }]}
							>
								<Select
									mode="multiple"
									placeholder="Select"
									size="large"
									showSearch
									disabled={!isEditMode}
									onChange={handleVehicleTypeChange}
								>
									{vehicleTypeOptions.length > 0 && (
										<Option value={ALL_VEHICLE_TYPES_VALUE}>All</Option>
									)}
									{vehicleTypeOptions.map((type) => (
										<Option
											key={type.value}
											value={type.value}
											disabled={initialVehicleTypeIds.includes(type.value)}
										>
											{type.label}
										</Option>
									))}
								</Select>
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item
								label="Mobile Number"
								name="mobile_number"
								rules={[
									{ required: true, message: 'Please enter mobile number' },
									{
										pattern: /^[0-9]{10}$/,
										message: 'Please enter a valid 10-digit mobile number',
									},
								]}
							>
								<Input
									placeholder="Eg : 9876545678"
									size="large"
									maxLength={20}
									disabled={!isEditMode}
								/>
							</Form.Item>
						</FormItem>
					</FormRow>

					<FormRow>
						<FormItem>
							<Form.Item
								label="Driving License Number"
								name="driving_license_number"
								rules={[
									{
										required: true,
										message: 'Please enter driving license number',
									},
									{
										validator(_, value) {
											if (value && value !== value.trimStart()) {
												return Promise.reject(
													new Error(
														'Driving license number should not have leading spaces'
													)
												);
											}
											return Promise.resolve();
										},
									},
								]}
							>
								<Input
									placeholder="Eg : DL0208735"
									size="large"
									disabled={!isEditMode}
								/>
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item
								label="Driving License Upload"
								required
								rules={[
									{
										validator: () => {
											if (!drivingLicenseFileKey) {
												return Promise.reject(
													new Error('Please upload driving license')
												);
											}
											return Promise.resolve();
										},
									},
								]}
							>
								<FileUploadContainer>
									<input
										ref={fileInputRef}
										type="file"
										accept=".pdf,.png,.jpeg,.jpg"
										onChange={handleFileChange}
										style={{ display: 'none' }}
									/>
									<BrowseButton
										type="button"
										icon={<UploadOutlined />}
										onClick={() => fileInputRef.current?.click()}
										loading={uploadingLicense}
										disabled={uploadingLicense || !isEditMode}
									>
										{uploadingLicense ? 'Uploading...' : 'Browse File'}
									</BrowseButton>
									{drivingLicenseFile && (
										<Text
											style={{
												display: 'block',
												marginTop: '8px',
												fontSize: '12px',
												color: '#52c41a',
											}}
										>
											{drivingLicenseFile.name}
										</Text>
									)}
									{drivingLicenseFileKey && !drivingLicenseFile && (
										<Flex alignItems="center" mt={2} gap={2}>
											<FilePdfOutlined
												style={{ fontSize: 20, color: '#ff4d4f' }}
											/>
											<Text fontSize={13} color="#333">
												Existing file uploaded
											</Text>
											<Button
												type="text"
												size="small"
												icon={<EyeOutlined />}
												onClick={() =>
													handleViewDocument(
														drivingLicenseFileKey,
														'Driving License Document'
													)
												}
												style={{
													padding: '4px 8px',
													height: 'auto',
													display: 'flex',
													alignItems: 'center',
												}}
											/>
										</Flex>
									)}
								</FileUploadContainer>
							</Form.Item>
						</FormItem>
					</FormRow>

					<FormRow>
						<FormItem>
							<Form.Item
								label="Valid Till"
								name="driving_license_valid_till"
								rules={[
									{ required: true, message: 'Please select valid till date' },
								]}
							>
								<DatePicker
									placeholder="Choose Date"
									size="large"
									style={{ width: '100%' }}
									format="DD-MM-YYYY"
									disabled={!isEditMode}
								/>
							</Form.Item>
						</FormItem>
						<FormItem />
					</FormRow>
				</Form>

				{isEditMode && (
					<ActionButtons>
						<CancelButton onClick={handleCancel}>Cancel</CancelButton>
						<SaveButton onClick={handleSubmit} loading={saving}>
							Save Changes
						</SaveButton>
					</ActionButtons>
				)}
			</FormContainer>

			<TransferDriverModal
				visible={transferModalVisible}
				onCancel={handleTransferCancel}
				onConfirm={handleTransferConfirm}
				driverData={driverDataForTransfer}
				loading={transferring}
			/>

			<DocumentViewerModal
				visible={documentViewerVisible}
				onClose={handleCloseDocumentViewer}
				documentUrl={viewingDocumentUrl}
				documentName={viewingDocumentName}
				documentType={viewingDocumentType}
			/>
		</PageContainer>
	);
}
